import { ExtractTypeFromTRPCSubscription } from '@pelicargo/types';
import { useEffect, useState } from 'react';

import { trpc, TrpcInputs, TrpcOutputs } from '../../config/trpc';
import { Request, UseResource } from '../../utils/types';

export type RequestInput = NonNullable<TrpcInputs['updateRequest']>;
export type RequestOutput = TrpcOutputs['updateRequest'];

export type GetRequestOutput = TrpcOutputs['getRequest'];

type RequestUpdatedPayload = ExtractTypeFromTRPCSubscription<TrpcOutputs['onRequestUpdated']>['request'];

/**
 * The cached request is written by `getRequest` and then overwritten wholesale by
 * `onRequestUpdated`, so it only ever holds what the *last* payload carried.
 *
 * The two used to disagree on `default_sender_contact` — the query pulled `user`, the subscription
 * only `associations` — so `user` disappeared on the first live update and every email drawer
 * gating on `default_sender_contact?.user?.id` silently stopped resolving the sender. The
 * subscription now includes both, so that relation is safe.
 *
 * It is still a `Partial`, and honestly so: the two payloads continue to differ elsewhere.
 * Dropping the `Partial` fails on both `setData` calls, and typing the consumer
 * (`CurrentRequestContext`) as a partial instead cascades to ~30 sites that forward `request` into
 * `RequireOnly<Request, …>` props. Making this non-partial means first unifying the `getRequest` /
 * `updateRequest` / `onRequestUpdated` includes in `api/` — worth doing, but its own change.
 */
export type CachedRequest = Partial<Omit<Request, 'default_sender_contact'>> & {
  default_sender_contact?: Partial<Request['default_sender_contact']> &
    Partial<RequestUpdatedPayload['default_sender_contact']>;
};

type UseRequest = UseResource<CachedRequest, RequestInput, RequestOutput>;

export const useRequest = (id: number, initialRequest?: CachedRequest): UseRequest => {
  const getRequest = trpc.getRequest.useQuery({ id });
  const updateRequest = trpc.updateRequest.useMutation();

  const [error, setError] = useState<string>();
  const [data, setData] = useState<CachedRequest>(initialRequest);

  useEffect(() => {
    if (getRequest?.data) setData(getRequest?.data);
  }, [getRequest.data]);

  useEffect(() => {
    const errors: string[] = [];
    if (getRequest.error) errors.push(getRequest.error?.message);
    if (updateRequest.error) errors.push(updateRequest.error?.message);

    if (errors?.length > 0) setError(errors.join(', '));
  }, [getRequest.error, updateRequest.error]);

  trpc.onRequestUpdated.useSubscription(
    { id },
    {
      onData: (newData) => {
        setData(newData.request);
      },
      onError: (err: any) => {
        setError(err?.message);
      },
    },
  );

  const isLoading = getRequest?.isLoading || updateRequest?.isLoading;
  return {
    data,
    isLoading,
    error,
    mutate: (args) => updateRequest.mutateAsync({ id, ...args } as any),
  };
};
